import OnlineIndicator from "@features/presence/components/OnlineIndicator";
import { GroupMenuIcon } from "@shared/components/ui/Icon";
import { getProfileThumbnail, useProfile } from "@shared/hooks/useProfile";
import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import { Link } from "react-router-dom";
import { MessagesHeaderProps } from "../types";

function MessagesHeader({
  msgSidebarOpen,
  setMsgSidebarOpen,
  selectedUser,
  isTyping,
  selectedGroup,
  userProfiles,
  onEditGroup,
}: Omit<MessagesHeaderProps, "selectedUser" | "userProfiles"> & {
  selectedUser?: User | null;
  userProfiles: Record<string, User>;
  onEditGroup?: (group: any) => void;
}) {
  const { user } = useProfile();
  return (
    <div className="sticky top-0">
      <div
        className={`flex items-center justify-between before:absolute before:inset-0 before:backdrop-blur-md before:bg-gray-50/90 dark:before:bg-[#151D2C]/90 before:-z-10 border-b ${theme.border.primary} px-4 sm:px-6 md:px-5 h-16`}
      >
        {/* People */}
        <div className="flex items-center">
          {/* Close button */}
          <button
            className={`md:hidden text-gray-400 hover: ${theme.text.muted}`}
            onClick={() => setMsgSidebarOpen(!msgSidebarOpen)}
            aria-controls="messages-sidebar"
            aria-expanded={msgSidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg
              className="w-6 h-6 fill-current"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          {/* Show group name if group is selected, if not, direct user */}
          {selectedGroup ? (
            <div className="ml-2">
              <h3 className="font-madefor text-lg font-semibold mt-3">
                {selectedGroup.title}
              </h3>
              {selectedGroup.participants &&
                selectedGroup.participants.length > 0 &&
                userProfiles && (
                  <div className="flex items-center mb-3 space-x-1 max-w-xs overflow-hidden">
                    {selectedGroup.participants
                      .map((id: string) => userProfiles[id])
                      .filter(Boolean)
                      .map((profile, idx) => (
                        <Link
                          to={`/community/profile/${profile.id}`}
                          key={profile.id || idx}
                          className="p-0.5 shrink-0 rounded-full border border-white dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
                          title={profile.profile.name}
                        >
                          <img
                            src={getProfileThumbnail(profile.profile)}
                            alt={profile.profile.name}
                            className="w-6 h-6 rounded-full object-cover group-hover:ring-2 group-hover:ring-blue-400"
                          />
                        </Link>
                      ))}
                  </div>
                )}
              {isTyping && Object.values(isTyping).some(Boolean) && (
                <span className="ml-2 text-xs text-gray-500">typing...</span>
              )}
            </div>
          ) : (
            selectedUser && (
              <>
                <div className="flex items-center">
                  <div className="relative mr-2">
                    <img
                      className="rounded-full border-2 border-white dark:border-gray-800 box-content"
                      src={getProfileThumbnail(selectedUser.profile)}
                      width="32"
                      height="32"
                      alt={selectedUser.profile?.name}
                    />
                    <OnlineIndicator
                      userId={selectedUser.id}
                      className="absolute bottom-0 right-0"
                    />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {selectedUser.profile?.name}
                    </span>
                    {Object.values(isTyping).some(Boolean) && (
                      <div className={`text-left text-xs ${theme.text.muted}`}>
                        Typing...
                      </div>
                    )}
                  </div>
                </div>
              </>
            )
          )}
        </div>
        {/* Right click only for direct user */}
        {selectedUser && !selectedGroup && (
          <div className="flex">
            <Link
              to={"/community/profile/" + selectedUser.id}
              className={`p-1.5 shrink-0 rounded-lg ${theme.bg.primary} border ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 shadow-sm ml-2`}
            >
              <svg
                className={`fill-current text-gray-400 dark: ${theme.text.muted}`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
              </svg>
            </Link>
          </div>
        )}
        {/* Menu button for group */}
        {selectedGroup && selectedGroup.createdBy === user?.id && (
          <div className="flex">
            <button
              className={`p-1.5 shrink-0 rounded-lg ${theme.bg.primary} border ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 shadow-sm ml-2`}
              aria-label="Group menu"
              type="button"
              onClick={() =>
                onEditGroup && selectedGroup && onEditGroup(selectedGroup)
              }
            >
              <GroupMenuIcon
                size={20}
                className={`fill-current text-gray-400 dark:${theme.text.muted}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessagesHeader;
